import settings from "../../settings"
import visit from "../../visit"
import createRequestBuilder from "@/core/request"
import { pageTagging, pageTaggingAsync } from "@/core/tagging"
import { findForcedSegments } from "@/core/tagging/extract"
import { injectResponseData } from "@/core/api/event/handlers"
import { EventPreviewMessage, EventResponseMessage } from "@/types"
import context from "@/core/context"
import windows from "@/core/windows"
import { fetch } from "@/core/fetch"

export type EventPreviewParams = {
  divId: string
  recoId?: string
  templateData: string
}

export const TEMPLATE_PREVIEW_PARAM = "nostotemplatepreview"

function prunedUrl() {
  const url = new URL(`${settings.server}/template/storefront/preview`)
  const customerId = visit.getCustomerId()
  if (customerId) {
    url.searchParams.append("c", customerId)
  }
  url.searchParams.append("m", settings.account)
  return url.toString()
}

/**
 * Performs a post request which matches the specifications simple requests format to avoid preflight requests.
 * For this reason content-type is fixed to text/plain.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS#Simple_requests
 */
function getTemplatePreviewRecommendation(data: EventPreviewMessage) {
  return fetch(prunedUrl(), {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body: JSON.stringify(data)
  })
}

async function loadPreview(previewParams: EventPreviewParams) {
  const { divId, templateData } = previewParams

  if (templateData && divId) {
    const request = createRequestBuilder().enablePreview()
    const data = pageTagging()
    const forcedSegments = findForcedSegments() || []
    request.populateFrom({ data, forcedSegments })
    request.setElements([divId])

    const response = await getTemplatePreviewRecommendation({ ...request.getData(), ...previewParams })
    const responseData = (await response.json()) as EventResponseMessage
    injectResponseData(responseData, {})
  }
}

function isValidOrigin(eventOrigin: string) {
  if (settings.server === eventOrigin) {
    return true
  }

  const domainSuffix = settings.server.split(".").splice(1).join(".")
  const patternStr = `^https://(my|connect).${domainSuffix}$`
  return new RegExp(patternStr).test(eventOrigin)
}

function setupTemplatePreviewListener() {
  windows.site.addEventListener("message", (event: MessageEvent<EventPreviewParams>) => {
    if (!isValidOrigin(event.origin)) {
      return
    }

    loadPreview(event.data)
  })
}

export function isRecommendationTemplatePreview() {
  return !!context.siteUrl.searchParams.get(TEMPLATE_PREVIEW_PARAM)
}

export async function initTemplatePreview() {
  if (!isRecommendationTemplatePreview()) {
    return
  }

  await pageTaggingAsync()
  setupTemplatePreviewListener()
}
